import { useEffect, useState } from 'react';
import { api, humanise, type PersonDriverRow, type RiderRow } from '../api';
import { PageHead, Panel, State, kycTone } from '../ui';
import { Person } from './Person';

/**
 * The support dashboard's lookup: find any rider or driver by phone or name,
 * see their routes and rides, open a ride, and message them.
 */
export function People({ onConversation }: { onConversation: (ticketId: string) => void }) {
  const [q, setQ] = useState('');
  const [res, setRes] = useState<{ riders: RiderRow[]; drivers: PersonDriverRow[] } | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (q.trim().length < 2) { setRes(null); return; }
    const t = setTimeout(() => {
      api.findPeople(q).then((r) => { setRes(r); setErr(null); }).catch((e) => setErr(e instanceof Error ? e.message : 'Search failed'));
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const message = async (userId: string, tripId?: string) => {
    try {
      const c = await api.startConversation(userId, tripId);
      onConversation(c.id);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not start the conversation');
    }
  };

  if (open) return <Person id={open} mode="support" onClose={() => setOpen(null)} onMessage={(u, t) => void message(u, t)} />;

  return (
    <>
      <PageHead
        title="Find a rider or driver"
        blurb="Search by phone number or name. Open someone to see their routes, rides and trips, open any ride, or message them."
      />
      <Panel title="Search">
        <div className="pad">
          <input type="search" autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Phone number or name" style={{ width: '100%', maxWidth: 420 }} />
          {err ? <p className="hint bad">{err}</p> : null}
        </div>
      </Panel>
      {res ? (
        <div className="support">
          <Panel title={`Riders (${res.riders.length})`}>
            {res.riders.length ? (
              <ul className="queue pad">
                {res.riders.map((r) => (
                  <li key={r.id}>
                    <button className="queue-item" onClick={() => setOpen(r.id)}>
                      <span className="who">{r.full_name ?? 'No name'} <small>· {r.phone}</small></span>
                      <span className="last">{r.commute ?? 'No commute set up'}</span>
                      <span className="tags">{Number(r.live_rides) ? <State tone="ok">ride booked</State> : null}</span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : <div className="empty">No riders match.</div>}
          </Panel>
          <Panel title={`Drivers (${res.drivers.length})`}>
            {res.drivers.length ? (
              <ul className="queue pad">
                {res.drivers.map((d) => (
                  <li key={d.id}>
                    <button className="queue-item" onClick={() => setOpen(d.id)}>
                      <span className="who">{d.full_name ?? 'No name'} <small>· {d.phone}</small></span>
                      <span className="last">{d.registration_no ? `${[d.make, d.model].filter(Boolean).join(' ')} · ${d.registration_no}` : 'No vehicle yet'}</span>
                      <span className="tags"><State tone={kycTone(d.kyc_status)}>{humanise(d.kyc_status)}</State></span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : <div className="empty">No drivers match.</div>}
          </Panel>
        </div>
      ) : null}
    </>
  );
}
